import React, { useState, useRef, FC, PropsWithChildren } from "react";
import { useSpring, animated } from "@react-spring/web";
import classNames from "classnames";
import {
  NativeProps,
  withNativeProps,
  mergeProps,
  renderToContainer,
  withStopPropagation,
  ShouldRender,
} from "@/utils/components-utils";
import Mask from "../mask";

import { defaultPopupBaseProps, PopupBaseProps } from "./popup-base-props";
import IconFont from "../IconFont";
import styles from "./popup.module.scss";

import useIsomorphicLayoutEffect from "@/hooks/useIsomorphicLayoutEffect";
import useUnmountedRef from "@/hooks/useUnmountedRef";
import { useLockScroll } from "@/hooks/useLockScroll";
import { useInnerVisible } from "@/hooks/useInnerVisible";

export type PopupProps = PopupBaseProps &
  PropsWithChildren<{
    position?: "bottom" | "top" | "left" | "right";
  }> &
  NativeProps;

const defaultProps = {
  ...defaultPopupBaseProps,
  position: "bottom",
};

export const Popup: FC<PopupProps> = (p) => {
  const props = mergeProps(defaultProps, p);
  const getBodyClassName = () => {
    switch (props.position) {
      case "top":
        return styles.rfPopup_position_top;
      case "left":
        return styles.rfPopup_position_left;
      case "right":
        return styles.rfPopup_position_right;
      case "bottom":
      default:
        return styles.rfPopup_position_bottom;
    }
  };

  const bodyCls = classNames(
    styles.rfPopup__body,
    props.bodyClassName,
    getBodyClassName()
  );

  const [active, setActive] = useState(props.visible);
  useIsomorphicLayoutEffect(() => {
    if (props.visible) {
      setActive(true);
    }
  }, [props.visible]);

  const ref = useRef<HTMLDivElement>(null);
  useLockScroll(ref, props.disableBodyScroll && active ? "strict" : false);

  const unmountedRef = useUnmountedRef();
  const { percent } = useSpring({
    percent: props.visible ? 0 : 100,
    config: {
      precision: 0.1,
      mass: 0.4,
      tension: 300,
      friction: 30,
    },
    onRest: () => {
      if (unmountedRef.current) return;
      setActive(props.visible);
      if (props.visible) {
        props.afterShow?.();
      } else {
        props.afterClose?.();
      }
    },
  });

  const maskVisible = useInnerVisible(active && props.visible);

  const node = withStopPropagation(
    props.stopPropagation,
    withNativeProps(
      props,
      <div
        className={styles.rfPopup}
        onClick={props.onClick}
        style={{ display: active ? undefined : "none" }}
      >
        {props.mask && (
          <Mask
            visible={maskVisible}
            forceRender={props.forceRender}
            destroyOnClose={props.destroyOnClose}
            onMaskClick={(e: any) => {
              props.onMaskClick?.(e);
              if (props.closeOnMaskClick) {
                props.onClose?.();
              }
            }}
            className={props.maskClassName}
            style={props.maskStyle}
            disableBodyScroll={false}
            stopPropagation={props.stopPropagation}
          />
        )}
        <animated.div
          className={bodyCls}
          style={{
            ...props.bodyStyle,
            transform: percent.to((v) => {
              if (props.position === "bottom") {
                return `translate(0, ${v}%)`;
              }
              if (props.position === "top") {
                return `translate(0, -${v}%)`;
              }
              if (props.position === "left") {
                return `translate(-${v}%, 0)`;
              }
              if (props.position === "right") {
                return `translate(${v}%, 0)`;
              }
              return "none";
            }),
          }}
          ref={ref}
        >
          {props.showCloseButton && (
            <a
              className={classNames(
                styles.rfPopup_close_icon,
                styles.rfPlainAnchor
              )}
              onClick={() => {
                props.onClose?.();
              }}
            >
              <IconFont type="close" />
            </a>
          )}
          {props.children}
        </animated.div>
      </div>
    )
  );

  return (
    <ShouldRender
      active={active}
      forceRender={props.forceRender}
      destroyOnClose={props.destroyOnClose}
    >
      {renderToContainer(props.getContainer, node)}
    </ShouldRender>
  );
};
